import { Fragment, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Paperclip, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useChatMessages, ChatContact } from '@/hooks/chat/useChat';
import { useAuth } from '@/contexts/AuthContext';
import { ChatMessage } from './ChatMessage';
import { toast } from 'sonner';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ChatRoomProps {
  contact: ChatContact;
  onBack: () => void;
}

// 7MB: o upload vai base64 dentro do JSON (~33% de inflação) e o body parser
// do server aceita 10mb — 10MB de arquivo estourava 413 com erro genérico.
const MAX_FILE_SIZE = 7 * 1024 * 1024;

const dayLabel = (date: Date): string => {
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  return format(date, "d 'de' MMMM", { locale: ptBR });
};

/**
 * Visualização de uma conversa específica
 */
export const ChatRoom = ({ contact, onBack }: ChatRoomProps) => {
  const { user } = useAuth();
  const [messageText, setMessageText] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    isLoading,
    hasOlder,
    loadOlder,
    sendMessage,
    markAsRead,
    uploadAttachment,
    getAttachmentUrl,
  } = useChatMessages(contact.user_id);

  // Marcar como lidas ao abrir
  useEffect(() => {
    markAsRead.mutate();
  }, [contact.user_id]);

  // Scroll para o final quando chega mensagem nova. Âncora no fim da lista:
  // o ref do ScrollArea (shadcn) aponta pro Root (overflow-hidden), não pro
  // Viewport — setar scrollTop nele não tinha efeito nenhum. Só rola quando a
  // ÚLTIMA mensagem muda ("Carregar anteriores" não deve pular pro fim).
  const lastMsgIdRef = useRef<string | null>(null);
  useEffect(() => {
    const last = messages[messages.length - 1]?.id ?? null;
    if (last && last !== lastMsgIdRef.current) {
      endRef.current?.scrollIntoView({ block: 'end' });
    }
    lastMsgIdRef.current = last;
  }, [messages]);

  const handleSend = async () => {
    if (!messageText.trim() && !pendingFile) return;
    if (isSending) return;

    setIsSending(true);

    try {
      let attachment: { path: string; name: string; type: string } | undefined;

      if (pendingFile) {
        const result = await uploadAttachment(pendingFile);
        if (result) {
          attachment = result;
        }
      }

      await sendMessage.mutateAsync({
        content: messageText.trim() || undefined,
        attachment,
      });

      setMessageText('');
      setPendingFile(null);
    } catch (error) {
      console.error('Erro ao enviar:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('Arquivo muito grande. Máximo: 7MB');
      return;
    }

    setPendingFile(file);
    e.target.value = '';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Voltar">
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary">
            {getInitials(contact.nome_completo)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate">{contact.nome_completo}</h3>
          {contact.setor && (
            <p className="text-xs text-muted-foreground">{contact.setor}</p>
          )}
        </div>
      </div>

      {/* Mensagens */}
      <ScrollArea className="flex-1 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-muted-foreground">Nenhuma mensagem ainda</p>
          </div>
        ) : (
          <div className="space-y-3">
            {hasOlder && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={loadOlder}
              >
                Carregar mensagens anteriores
              </Button>
            )}
            {messages.map((msg, i) => {
              const msgDate = new Date(msg.created_at);
              const prevDate = i > 0 ? new Date(messages[i - 1].created_at) : null;
              const newDay = !prevDate || msgDate.toDateString() !== prevDate.toDateString();
              return (
                <Fragment key={msg.id}>
                  {newDay && (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 border-t" />
                      <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
                        {dayLabel(msgDate)}
                      </span>
                      <div className="flex-1 border-t" />
                    </div>
                  )}
                  <ChatMessage
                    message={msg}
                    isOwn={msg.sender_id === user?.id}
                    onDownload={getAttachmentUrl}
                  />
                </Fragment>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </ScrollArea>

      {/* Preview do arquivo pendente */}
      {pendingFile && (
        <div className="px-4 py-2 border-t bg-muted/50">
          <div className="flex items-center gap-2 text-sm">
            <Paperclip className="h-4 w-4" />
            <span className="truncate flex-1">{pendingFile.name}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setPendingFile(null)}
              aria-label="Remover anexo"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />

          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
            aria-label="Anexar arquivo"
          >
            <Paperclip className="h-5 w-5" />
          </Button>

          <Textarea
            placeholder="Digite uma mensagem..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            rows={1}
            autoFocus
            className="flex-1 min-h-9 max-h-32 resize-none py-2"
          />

          <Button
            size="icon"
            onClick={handleSend}
            disabled={isSending || (!messageText.trim() && !pendingFile)}
            aria-label="Enviar mensagem"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
